import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

export interface Site {
  id: number;
  name: string;
  brand: string;
  address: string;
  phone: string | null;
  email: string | null;
  contactName: string | null;
  coverImagePath: string | null;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export function useAdminSites() {
  return useQuery<Site[]>({
    queryKey: ["admin-sites"],
    queryFn: async () => {
      const res = await fetch(`${import.meta.env.BASE_URL}api/admin/sites`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to fetch sites");
      return res.json();
    },
  });
}

export function useActiveSites() {
  return useQuery<Site[]>({
    queryKey: ["active-sites"],
    queryFn: async () => {
      const res = await fetch(`${import.meta.env.BASE_URL}api/ppm/sites`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to fetch active sites");
      return res.json();
    },
  });
}

export function useCreateSite() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: Partial<Site>) => {
      const res = await fetch(`${import.meta.env.BASE_URL}api/admin/sites`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || "Failed to create site");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: (query) => {
          const key = JSON.stringify(query.queryKey);
          return key.includes("admin-sites") || 
                 key.includes("active-sites") || 
                 key.includes("/api/admin/sites") || 
                 key.includes("/api/ppm/sites");
        }
      });
    },
  });
}

export function useUpdateSite() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<Site> }) => {
      const res = await fetch(`${import.meta.env.BASE_URL}api/admin/sites/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || "Failed to update site");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: (query) => {
          const key = JSON.stringify(query.queryKey);
          return key.includes("admin-sites") || 
                 key.includes("active-sites") || 
                 key.includes("/api/admin/sites") || 
                 key.includes("/api/ppm/sites");
        }
      });
    },
  });
}

export function useDeleteSite() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const res = await fetch(`${import.meta.env.BASE_URL}api/admin/sites/${id}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || "Failed to archive site");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: (query) => {
          const key = JSON.stringify(query.queryKey);
          return key.includes("admin-sites") || 
                 key.includes("active-sites") || 
                 key.includes("/api/admin/sites") || 
                 key.includes("/api/ppm/sites");
        }
      });
    },
  });
}

export async function uploadSiteCover(file: File): Promise<string> {
  const initRes = await fetch(`${import.meta.env.BASE_URL}api/admin/sites/cover-upload`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ name: file.name, size: file.size, contentType: file.type }),
  });
  if (!initRes.ok) {
    const b = await initRes.json().catch(() => ({}));
    throw new Error(b.error || "Failed to initialize upload");
  }
  const { uploadURL, objectPath } = await initRes.json();
  
  const uploadRes = await fetch(uploadURL, {
    method: "PUT",
    body: file,
    headers: { "Content-Type": file.type },
  });
  
  if (!uploadRes.ok) throw new Error("Upload failed");
  
  return objectPath;
}
